import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingWhatsApp from '../../components/feature/FloatingWhatsApp';
import { WHATSAPP_URL } from '../../constants/whatsapp';
import { getOrganizationSchema, getWebPageSchema, SCHEMA_SITE_URL } from '@/constants/schema';

const SITE_URL = import.meta.env.VITE_SITE_URL as string;

const PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    getOrganizationSchema(),
    getWebPageSchema({
      path: '/food-grade-gloves',
      name: 'Food Grade Gloves Wholesale Oman | Vinyl Nitrile Food Safe | omandisposables.com',
      description: 'Vinyl, polyethylene & nitrile food-safe gloves wholesale for restaurants, hotels and food processing in Oman & GCC. Trusted supply.',
      breadcrumbId: `${SCHEMA_SITE_URL}/food-grade-gloves#breadcrumb`,
    }),
    {
      '@type': 'BreadcrumbList',
      '@id': `${SCHEMA_SITE_URL}/food-grade-gloves#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Food Grade Gloves', item: `${SITE_URL}/food-grade-gloves` },
      ],
    },
    {
      '@type': 'Product',
      '@id': `${SCHEMA_SITE_URL}/food-grade-gloves#product`,
      'name': 'Food Grade Gloves Wholesale Oman — Vinyl, PE & Nitrile',
      'description': 'Wholesale food-safe vinyl, polyethylene and nitrile disposable gloves for restaurants, hotels and food processing in Oman and GCC. Powder-free and latex-free options. Est. 1996.',
      'image': 'https://public.readdy.ai/ai/img_res/5e5440d0273449421c662e7b03e089f2.jpg',
      'brand': { '@type': 'Brand', 'name': 'Oman Disposables' },
      'category': 'Food Grade Gloves',
      'url': `${SCHEMA_SITE_URL}/food-grade-gloves`,
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'OMR',
        'availability': 'https://schema.org/InStock',
        'areaServed': ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'],
        'seller': { '@type': 'Organization', 'name': 'Oman Disposables' },
      },
    },
    {
      '@type': 'ItemList',
      name: 'Food Grade Gloves Range — omandisposables.com',
      description: 'Vinyl, polyethylene and nitrile food-safe gloves for restaurants, hotels and food processing in Oman and GCC.',
      url: `${SITE_URL}/food-grade-gloves`,
      numberOfItems: 4,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@type': 'Product',
            name: 'Vinyl Food Handling Gloves',
            description: 'Clear vinyl gloves for food service, buffets, sandwich preparation and kitchen use. Food-safe.',
            image: 'https://readdy.ai/api/search-image?query=clear%20transparent%20vinyl%20food%20handling%20gloves%20being%20used%20in%20commercial%20kitchen%20food%20preparation%2C%20chef%20wearing%20disposable%20gloves%20preparing%20food%2C%20clean%20kitchen%20environment&width=600&height=420&seq=food-vinyl-01&orientation=landscape',
            url: `${SITE_URL}/food-grade-gloves`,
            category: 'Food Grade Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@type': 'Product',
            name: 'Polyethylene (PE) Gloves',
            description: 'Lightweight polyethylene gloves ideal for short-term food handling, bakeries, and deli counters.',
            image: 'https://readdy.ai/api/search-image?query=transparent%20polyethylene%20PE%20gloves%20roll%20dispenser%20on%20bakery%20counter%2C%20food%20safe%20lightweight%20disposable%20gloves%20in%20commercial%20food%20service%20setting&width=600&height=420&seq=food-pe-01&orientation=landscape',
            url: `${SITE_URL}/food-grade-gloves`,
            category: 'Food Grade Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@type': 'Product',
            name: 'Nitrile Food Grade Gloves',
            description: 'Premium nitrile gloves for food processing facilities — chemical resistant, oil-proof, durable.',
            image: 'https://readdy.ai/api/search-image?query=blue%20nitrile%20food%20processing%20gloves%20in%20professional%20food%20manufacturing%20facility%2C%20food%20safe%20nitrile%20gloves%20in%20industrial%20food%20processing%20line&width=600&height=420&seq=food-nitrile-01&orientation=landscape',
            url: `${SITE_URL}/food-grade-gloves`,
            category: 'Food Grade Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
        {
          '@type': 'ListItem',
          position: 4,
          item: {
            '@type': 'Product',
            name: 'Catering & Hospitality Packs',
            description: 'Bulk glove packs for hotels, catering companies and restaurant chains in GCC.',
            image: 'https://readdy.ai/api/search-image?query=hotel%20catering%20staff%20wearing%20disposable%20gloves%20serving%20food%20at%20buffet%2C%20professional%20hospitality%20food%20service%20setting%20with%20elegant%20table%20setup&width=600&height=420&seq=food-catering-01&orientation=landscape',
            url: `${SITE_URL}/food-grade-gloves`,
            category: 'Food Grade Gloves',
            brand: { '@type': 'Brand', name: 'omandisposables.com' },
            offers: { '@type': 'Offer', seller: { '@type': 'Organization', name: 'omandisposables.com' }, availability: 'https://schema.org/InStock', priceCurrency: 'OMR', areaServed: ['OM', 'AE', 'SA', 'KW', 'BH', 'QA'] },
          },
        },
      ],
    },
  ],
};

const PRODUCTS = [
  {
    name: 'Vinyl Food Handling Gloves',
    nameAr: 'قفازات فينيل لمناولة الأغذية',
    desc: 'Clear vinyl gloves for food service, buffets, sandwich preparation and kitchen use. Food-safe and comfortable.',
    descAr: 'قفازات فينيل شفافة لخدمات الأغذية والبوفيه وتحضير السندويشات والاستخدام في المطبخ. آمنة للأغذية.',
    specs: ['Food Grade', 'Powder-Free', 'Clear/Blue Colors', 'S–XL Sizes'],
    img: 'https://readdy.ai/api/search-image?query=clear%20transparent%20vinyl%20food%20handling%20gloves%20being%20used%20in%20commercial%20kitchen%20food%20preparation%2C%20chef%20wearing%20disposable%20gloves%20preparing%20food%2C%20clean%20kitchen%20environment&width=600&height=420&seq=food-vinyl-01&orientation=landscape',
    tag: 'Food Safe',
  },
  {
    name: 'Polyethylene (PE) Gloves',
    nameAr: 'قفازات البولي إيثيلين',
    desc: 'Lightweight polyethylene gloves ideal for short-term food handling, bakeries, and deli counters.',
    descAr: 'قفازات بولي إيثيلين خفيفة الوزن مثالية للتعامل قصير المدى مع الطعام والمخابز وعدادات الدلي.',
    specs: ['One-size', 'High Volume Rolls', 'Embossed Texture', 'Waterproof'],
    img: 'https://readdy.ai/api/search-image?query=transparent%20polyethylene%20PE%20gloves%20roll%20dispenser%20on%20bakery%20counter%2C%20food%20safe%20lightweight%20disposable%20gloves%20in%20commercial%20food%20service%20setting&width=600&height=420&seq=food-pe-01&orientation=landscape',
    tag: 'High Volume',
  },
  {
    name: 'Nitrile Food Grade Gloves',
    nameAr: 'قفازات نيتريل غذائية',
    desc: 'Premium nitrile gloves for food processing facilities — chemical resistant, oil-proof, durable.',
    descAr: 'قفازات نيتريل فاخرة لمنشآت معالجة الأغذية — مقاومة للمواد الكيميائية ومقاومة للزيوت ومتينة.',
    specs: ['Chemical Resistant', 'Oil-Proof', 'Blue/Black', 'Food Processing Grade'],
    img: 'https://readdy.ai/api/search-image?query=blue%20nitrile%20food%20processing%20gloves%20in%20professional%20food%20manufacturing%20facility%2C%20food%20safe%20nitrile%20gloves%20in%20industrial%20food%20processing%20line&width=600&height=420&seq=food-nitrile-01&orientation=landscape',
    tag: 'Food Processing',
  },
  {
    name: 'Catering & Hospitality Packs',
    nameAr: 'عبوات تقديم الطعام والضيافة',
    desc: 'Bulk packs for hotels, catering companies and restaurant chains. Mixed sizes, fast delivery.',
    descAr: 'عبوات بالجملة للفنادق وشركات تقديم الطعام وسلاسل المطاعم. مقاسات مختلطة وتسليم سريع.',
    specs: ['Hotel Bulk Packs', 'Mixed Size Boxes', 'White Label Available', 'GCC Supply'],
    img: 'https://readdy.ai/api/search-image?query=hotel%20catering%20staff%20wearing%20disposable%20gloves%20serving%20food%20at%20buffet%2C%20professional%20hospitality%20food%20service%20setting%20with%20elegant%20table%20setup&width=600&height=420&seq=food-catering-01&orientation=landscape',
    tag: 'Bulk Hospitality',
  },
];

const FOOD_FEATURES = [
  { icon: 'ri-leaf-line', en: 'Food Contact Safe', ar: 'آمن للتلامس مع الطعام' },
  { icon: 'ri-shield-check-line', en: 'Food Processing Grade', ar: 'درجة معالجة الأغذية' },
  { icon: 'ri-drop-line', en: 'Powder-Free Options', ar: 'خيارات بدون بودرة' },
  { icon: 'ri-temp-cold-line', en: 'Barrier Protection', ar: 'حماية حاجزة' },
  { icon: 'ri-recycle-line', en: 'Latex-Free Available', ar: 'خيارات خالية من اللاتكس' },
  { icon: 'ri-stack-line', en: 'Bulk Volume Supply', ar: 'توريد بكميات كبيرة' },
];

export default function FoodGradeGlovesPage() {
  return (
    <main>
      <title>Food Grade Gloves Wholesale Oman | Vinyl Nitrile Food Safe | omandisposables.com</title>
      <meta name="description" content="Vinyl, polyethylene & nitrile food-safe gloves wholesale for restaurants, hotels and food processing in Oman & GCC. Trusted bulk supply. Orders via WhatsApp." />
      <meta name="keywords" content="food grade gloves Oman, vinyl gloves wholesale Muscat, food safe gloves GCC, food safe nitrile gloves, food gloves supplier Oman" />
      <link rel="canonical" href={`${SITE_URL}/food-grade-gloves`} />
      <meta property="og:title" content="Food Grade Gloves Wholesale Oman | omandisposables.com" />
      <meta property="og:description" content="Food-safe gloves wholesale for restaurants, hotels and food processing in Oman & GCC. Trusted supply." />
      <meta property="og:url" content={`${SITE_URL}/food-grade-gloves`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://readdy.ai/api/search-image?query=food%20service%20worker%20in%20commercial%20kitchen%20wearing%20white%20disposable%20gloves%20preparing%20fresh%20food%2C%20clean%20professional%20kitchen%20environment%2C%20bright%20lighting%2C%20food%20safety%20standards&width=1200&height=630&seq=food-hero-og&orientation=landscape" />
      <meta property="og:site_name" content="omandisposables.com" />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Food Grade Gloves Wholesale Oman | omandisposables.com" />
      <meta name="twitter:description" content="Food-safe vinyl, PE & nitrile gloves wholesale for Oman & GCC restaurants and food processing." />
      <meta name="twitter:image" content="https://readdy.ai/api/search-image?query=food%20service%20worker%20in%20commercial%20kitchen%20wearing%20white%20disposable%20gloves%20preparing%20fresh%20food%2C%20clean%20professional%20kitchen%20environment%2C%20bright%20lighting%2C%20food%20safety%20standards&width=1200&height=630&seq=food-hero-og&orientation=landscape" />
      <meta name="twitter:site" content="@omandisposables" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(PAGE_SCHEMA) }}
      />

      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-16 bg-brand-green overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=food%20service%20worker%20in%20commercial%20kitchen%20wearing%20white%20disposable%20gloves%20preparing%20fresh%20food%2C%20clean%20professional%20kitchen%20environment%2C%20bright%20lighting%2C%20food%20safety%20standards&width=1920&height=600&seq=food-hero-01&orientation=landscape"
            alt="Food Grade Gloves Wholesale Oman - omandisposables.com"
            className="w-full h-full object-cover object-top opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-green/90 to-navy/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="inline-flex items-center gap-2 bg-white/20 border border-white/30 text-white text-sm font-semibold px-4 py-2 rounded-full mb-6">
            🏅 Trusted Food-Safe Gloves Supplier from Oman
          </div>
          <h1 className="font-heading font-black text-white text-4xl md:text-5xl lg:text-6xl mb-3">
            Food Grade Gloves Wholesale
          </h1>
          <p className="font-arabic text-white/85 text-2xl md:text-3xl font-bold mb-4" dir="rtl">
            قفازات المواد الغذائية بالجملة
          </p>
          <p className="text-white/85 text-base md:text-lg max-w-2xl mb-8">
            Food-safe vinyl, polyethylene &amp; nitrile gloves for restaurants, hotels,
            food processing and catering across Oman and GCC.
          </p>
          <p className="font-arabic text-white/70 text-base max-w-2xl mb-8 text-right" dir="rtl">
            قفازات فينيل وبولي إيثيلين ونيتريل آمنة للأغذية للمطاعم والفنادق
            والصناعات الغذائية والتموين في عُمان والخليج.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-whatsapp text-base px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-transform inline-flex"
          >
            <i className="ri-whatsapp-line text-xl" />
            💬 WhatsApp for Food Gloves Pricing
          </a>
        </div>
      </section>

      {/* Trust bar */}
      <div className="bg-navy py-3 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-6 text-white text-sm font-semibold">
          {['Food Safe', 'Powder-Free', 'Latex-Free Options', 'Bulk Ready'].map((t) => (
            <span key={t} className="flex items-center gap-1">
              <i className="ri-checkbox-circle-fill text-brand-green" />{t}
            </span>
          ))}
        </div>
      </div>

      {/* Products */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-12">
            <h2 className="section-title-en text-3xl md:text-4xl mb-2">Our Food Grade Glove Range</h2>
            <p className="section-title-ar text-2xl md:text-3xl" dir="rtl">مجموعة قفازات المواد الغذائية لدينا</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-product-shop>
            {PRODUCTS.map((p) => (
              <div key={p.name} className="bg-white border border-gray-100 rounded-xl overflow-hidden hover:border-brand-green/30 hover:shadow-md transition-all">
                <div className="w-full h-52 overflow-hidden">
                  <img src={p.img} alt={`${p.name} - omandisposables.com Oman`} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <h3 className="font-heading font-bold text-navy text-lg">{p.name}</h3>
                      <p className="font-arabic text-gray-500 text-sm" dir="rtl">{p.nameAr}</p>
                    </div>
                    <span className="shrink-0 bg-brand-green/10 text-brand-green text-xs font-semibold px-3 py-1 rounded-full">{p.tag}</span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-2">{p.desc}</p>
                  <p className="font-arabic text-gray-500 text-xs text-right mb-4 leading-relaxed" dir="rtl">{p.descAr}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {p.specs.map((s) => (
                      <span key={s} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2.5 py-1 rounded-full">{s}</span>
                    ))}
                  </div>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp text-sm px-4 py-2.5 rounded-full w-full justify-center"
                  >
                    <i className="ri-whatsapp-line" />
                    Get Price &amp; Availability
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="text-center mb-10">
            <h2 className="section-title-en text-2xl md:text-3xl mb-2">Food Safety Features</h2>
            <p className="section-title-ar text-xl md:text-2xl" dir="rtl">مميزات سلامة الأغذية</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {FOOD_FEATURES.map((f) => (
              <div key={f.en} className="flex flex-col items-center text-center p-5 bg-white rounded-xl border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-green/8 mb-3">
                  <i className={`${f.icon} text-2xl text-brand-green`} />
                </div>
                <p className="font-heading font-semibold text-navy text-xs">{f.en}</p>
                <p className="font-arabic text-gray-400 text-xs mt-0.5" dir="rtl">{f.ar}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-navy text-center">
        <div className="container-max">
          <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/40 text-brand-green text-sm font-semibold px-4 py-2 rounded-full mb-6">
            🏅 جودة موثوقة. راسخون في عُمان. | Quality. Rooted in Oman.
          </div>
          <h2 className="font-heading font-black text-white text-3xl md:text-4xl mb-3">
            Order Food Grade Gloves Wholesale
          </h2>
          <p className="font-arabic text-white/80 text-xl font-bold mb-6" dir="rtl">
            طلب قفازات المواد الغذائية بالجملة
          </p>
          <p className="text-white/70 text-base max-w-lg mx-auto mb-8">
            Hotels, restaurants, food processors — get bulk pricing via WhatsApp in 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp text-base px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-transform w-full sm:w-auto justify-center"
            >
              <i className="ri-whatsapp-line text-xl" />
              💬 WhatsApp for Wholesale Price
            </a>
            <Link
              to="/request-quote"
              className="bg-white/10 border border-white/20 text-white font-heading font-semibold px-8 py-4 rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer w-full sm:w-auto flex items-center justify-center gap-2"
            >
              📋 Request a Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
